"use client";

import Image from "next/image";
import styles from "./card-preview.module.css";
import { usePageStore } from "../../usePageStore";
import {
  MdArrowBackIosNew,
  MdArrowForwardIos,
  MdClose,
  MdLocationOn,
  MdOutlineVideoCameraBack,
  MdPhotoLibrary,
  MdOutlineArchitecture,
  MdPictureAsPdf,
  MdOutlineFileDownload,
} from "react-icons/md";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BsBadge3D } from "react-icons/bs";
import MapWrapper from "./map-view/map-wrapper";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";
import { DeviceType } from "@/hooks/use-device-type";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { useLocale, useTranslations } from "next-intl";
import ContactDialog from "@/components/contact-agent-info/seller-contact-dialog";
import {
  ContactDialogType,
  Currencies,
  PurposeEnum,
  UserTypes,
} from "@/types/types";
import { useCurrenciesContext } from "@/global-context/currencies-context";
import { formatPrice } from "@/lib/price-formatter";
import { makeLocalizedHref } from "@/lib/localizedRoutes/routeHelpers";
import { AnimatePresence, motion } from "framer-motion";
import { getMedia } from "@/lib/media/shared";
import { useDialogRouting } from "@/hooks/use-dialog-routing";

export default function CardPreview() {
  const previewListingState = usePageStore(
    (state) => state.previewListingState,
  );
  const setPreviewListingState = usePageStore(
    (state) => state.setPreviewListingState,
  );
  const previewRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const { deviceType, openDialog } = usePageStore();
  const { userData } = useUserAuthDataContext();
  const locale = useLocale();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const { currenciesRates } = useCurrenciesContext();
  const currencyKey = `EUR-${userData.currency}` as keyof Currencies;
  const rate: number = currenciesRates[currencyKey];
  const t = useTranslations("ListingCardPreview");

  // --- MEDIA RESOLUTION & GROUPING ---
  const photosArray = useMemo(() => {
    if (!previewListingState.listing?.photos) return [];
    return Object.entries(previewListingState.listing.photos)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([_, item]) => item);
  }, [previewListingState.listing?.photos]);

  const floorPlansArray = useMemo(() => {
    return photosArray.filter((p) => p.room_tag === "floor_plans");
  }, [photosArray]);

  const resolvedVideo = useMemo(() => {
    if (!previewListingState.listing?.presentation_video_data) return null;
    return getMedia(previewListingState.listing.presentation_video_data, "md");
  }, [previewListingState.listing?.presentation_video_data]);

  const resolvedBrochure = useMemo(() => {
    if (!previewListingState.listing?.brochure) return null;
    return getMedia(previewListingState.listing.brochure);
  }, [previewListingState.listing?.brochure]);

  const firstPhotoResolved = useMemo(() => {
    if (photosArray.length === 0) return null;
    return getMedia(photosArray[0], "md");
  }, [photosArray]);

  // --- EVENT LISTENERS ---
  const isOpen =
    previewListingState.listing !== null &&
    (previewListingState.isMapLocationActive ||
      previewListingState.isPhotoGalleryActive ||
      previewListingState.isVideoPreviewActive ||
      previewListingState.isWebTourActive ||
      previewListingState.isFloorPlansActive ||
      previewListingState.isBrochureActive);

  const handleListingClick = async () => {
    if (previewListingState.listing === null) {
      return;
    }
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
    const newUrl = `/${locale}/listing/${previewListingState.listing.listing_id}`;
    if (deviceType === DeviceType.desktop) {
      window.open(newUrl, "_blank", "noopener,noreferrer");
    } else {
      setPreviewListingState(null, false, false, false, false, false, false);
      window.history.pushState(null, "", newUrl);
      await openDialog(
        previewListingState.listing.listing_id,
        locale,
        userData.sub,
      );
    }
  };

  const handleUserClick = async () => {
    if (previewListingState.listing === null) {
      return;
    }
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
    if (previewListingState.listing.creator.user_type === UserTypes.AGENCY) {
      const newUrl = makeLocalizedHref(locale, "agency-profile", [
        previewListingState.listing.created_by,
      ]);
      window.open(newUrl, "_blank", "noopener,noreferrer");
    } else {
      const newUrl = makeLocalizedHref(locale, "profiles", [
        previewListingState.listing.created_by,
      ]);
      window.open(newUrl, "_blank", "noopener,noreferrer");
    }
  };

  const closePreview = useCallback(() => {
    setPreviewListingState(null, false, false, false, false, false, false);
  }, [setPreviewListingState]);

  useDialogRouting("CardPreview", isOpen, closePreview);

  // --- FRAMER MOTION & VIEWS ---
  const viewOrder = [
    "photos",
    "floorplans",
    "video",
    "webtour",
    "brochure",
    "map",
    "none",
  ] as const;
  type ViewType = (typeof viewOrder)[number];
  const getInitialView = (): ViewType => {
    if (previewListingState.isWebTourActive) return "webtour";
    if (previewListingState.isVideoPreviewActive) return "video";
    if (previewListingState.isMapLocationActive) return "map";
    if (previewListingState.isFloorPlansActive) return "floorplans";
    if (previewListingState.isBrochureActive) return "brochure";
    if (previewListingState.isPhotoGalleryActive) return "photos";
    return "none";
  };

  const [activeView, setActiveView] = useState<ViewType>("none");
  const [direction, setDirection] = useState<1 | -1>(1); // 1 = down, -1 = up

  const handleChangeView = (next: ViewType) => {
    const currentIndex = viewOrder.indexOf(activeView);
    const nextIndex = viewOrder.indexOf(next);
    const newDirection = nextIndex > currentIndex ? 1 : -1;
    setDirection(newDirection);
    setActiveView(next);
  };

  const variants = {
    enter: (direction: 1 | -1) => ({
      y: direction === 1 ? 60 : -60,
      opacity: 0,
    }),
    center: { y: 0, opacity: 1 },
    exit: (direction: 1 | -1) => ({
      y: direction === 1 ? -60 : 60,
      opacity: 0,
    }),
  };

  useEffect(() => {
    if (!previewListingState.listing) {
      setActiveView("none");
      return;
    }
    setActiveView(getInitialView());
  }, [
    previewListingState.listing?.listing_id,
    previewListingState.isWebTourActive,
    previewListingState.isVideoPreviewActive,
    previewListingState.isMapLocationActive,
    previewListingState.isPhotoGalleryActive,
    previewListingState.isFloorPlansActive,
    previewListingState.isBrochureActive,
  ]);

  return (
    <>
      {previewListingState.listing !== null &&
        (previewListingState.isMapLocationActive ||
          previewListingState.isPhotoGalleryActive ||
          previewListingState.isVideoPreviewActive ||
          previewListingState.isWebTourActive ||
          previewListingState.isFloorPlansActive || // 🚀 Added this!
          previewListingState.isBrochureActive) && (
          <div ref={previewRef} className={styles.previewModal}>
            <div className={styles.listingDetailsWrapper}>
              <div className={styles.photoAndTitle}>
                <Image
                  className={styles.image}
                  src={firstPhotoResolved?.src || "/broken_image.webp"}
                  width={1200}
                  height={675}
                  alt={`Listing first photo.`}
                  draggable={false}
                  priority={true}
                  placeholder="blur"
                  blurDataURL="/placeholder_image.jpg"
                  onClick={(e) => {
                    e.preventDefault();
                    handleListingClick();
                  }}
                />
                <div className={styles.details}>
                  <h2
                    className={styles.previewTitle}
                    onClick={(e) => {
                      e.preventDefault();
                      handleListingClick();
                    }}
                  >
                    {previewListingState.listing.title}
                  </h2>
                  <div className={styles.userDetails}>
                    <Image
                      className={styles.profilePhoto}
                      src={previewListingState.listing.creator.photo_url}
                      width={128}
                      height={128}
                      alt={`User profile photo.`}
                      draggable={false}
                      priority={true}
                      placeholder="blur"
                      blurDataURL="/placeholder_image.jpg"
                      onClick={(e) => {
                        e.preventDefault();
                        handleUserClick();
                      }}
                    />
                    <div className={styles.nameAndContact}>
                      <p
                        className={styles.userName}
                        onClick={(e) => {
                          e.preventDefault();
                          handleUserClick();
                        }}
                      >
                        {previewListingState.listing.creator.name}
                      </p>
                      <span className={styles.lineSeparator}>–</span>
                      <button
                        className={styles.contactButton}
                        onClick={(e) => {
                          e.preventDefault();
                          setIsContactOpen(true);
                        }}
                      >
                        {t("contactAgent")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="priceWrapper">
                <button
                  className={styles.closeButton}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    closePreview();
                  }}
                >
                  <MdClose aria-hidden="true" />
                </button>
                <p className={styles.price}>
                  {formatPrice(previewListingState.listing.price * rate)}{" "}
                  {userData.currency}
                  {previewListingState.listing.purpose ===
                    PurposeEnum.FORRENT && (
                    <span className={styles.month}> / {t("month")}</span>
                  )}
                </p>
              </div>
            </div>
            <div className={styles.contentWrapper}>
              <div className={styles.viewContainer}>
                <AnimatePresence custom={direction} mode="wait">
                  <motion.div
                    key={activeView}
                    custom={direction}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.45, ease: "easeInOut" }}
                    className={styles.animatedView}
                  >
                    {activeView !== "none" && (
                      <>
                        {activeView === "photos" && photosArray.length > 0 && (
                          <div className={styles.galleryWrapper}>
                            <Swiper
                              resistanceRatio={0}
                              spaceBetween={0}
                              initialSlide={0}
                              slidesPerView={1}
                              modules={[Pagination]}
                              pagination={{
                                clickable: true,
                                dynamicBullets: true,
                              }}
                              className={styles.swiperDesktop}
                            >
                              {photosArray.map((photo, index) => {
                                const resolvedMedia = getMedia(photo, "lg");
                                const displaySrc =
                                  resolvedMedia?.src ||
                                  (photo as any).localUrl ||
                                  "/broken_image.webp";
                                const displayWebp = resolvedMedia?.webp;
                                console.log(displayWebp, resolvedMedia?.webp);
                                return (
                                  <SwiperSlide
                                    className={styles.swiperSlide}
                                    key={photo.MediaId || index}
                                  >
                                    <picture style={{ display: "contents" }}>
                                      {displayWebp && (
                                        <source
                                          srcSet={displayWebp}
                                          type="image/webp"
                                        />
                                      )}
                                      <Image
                                        loading={
                                          index !== 0 ? "lazy" : undefined
                                        }
                                        className={styles.swiperImage}
                                        src={displaySrc}
                                        width={2400}
                                        height={1350}
                                        unoptimized={true}
                                        alt="User uploaded photo."
                                        draggable={false}
                                        priority={index === 0 && true}
                                      />
                                    </picture>
                                  </SwiperSlide>
                                );
                              })}
                              <NextAndBackButtons />
                            </Swiper>
                          </div>
                        )}
                        {activeView === "floorplans" &&
                          floorPlansArray.length > 0 && (
                            <div className={styles.galleryWrapper}>
                              <Swiper
                                resistanceRatio={0}
                                spaceBetween={0}
                                initialSlide={0}
                                slidesPerView={1}
                                modules={[Pagination]}
                                pagination={{
                                  clickable: true,
                                  dynamicBullets: true,
                                }}
                                className={styles.swiperDesktop}
                              >
                                {floorPlansArray.map((plan, index) => {
                                  const resolvedMedia = getMedia(plan, "lg");
                                  const displaySrc =
                                    resolvedMedia?.src ||
                                    (plan as any).localUrl ||
                                    "/broken_image.webp";
                                  const displayWebp = resolvedMedia?.webp;
                                  return (
                                    <SwiperSlide
                                      className={styles.swiperSlide}
                                      key={plan.MediaId || index}
                                    >
                                      <picture style={{ display: "contents" }}>
                                        {displayWebp && (
                                          <source
                                            srcSet={displayWebp}
                                            type="image/webp"
                                          />
                                        )}
                                        <Image
                                          loading={
                                            index !== 0 ? "lazy" : undefined
                                          }
                                          className={styles.swiperImage}
                                          style={{ objectFit: "contain" }}
                                          src={displaySrc}
                                          width={2400}
                                          height={1350}
                                          unoptimized={true}
                                          alt="Floor Plan"
                                          draggable={false}
                                          priority={index === 0 && true}
                                        />
                                      </picture>
                                    </SwiperSlide>
                                  );
                                })}
                                <NextAndBackButtons />
                              </Swiper>
                            </div>
                          )}
                        {activeView === "brochure" && resolvedBrochure?.src && (
                          <div className={styles.brochureInner}>
                            <iframe
                              src={`${resolvedBrochure.src}#toolbar=0`}
                              className={styles.webTourFrame}
                              title={"Brochure Viewer"}
                            />
                            <a
                              href={resolvedBrochure.src}
                              download={
                                resolvedBrochure.name || "property-brochure.pdf"
                              }
                              className={styles.floatingDownload}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <MdOutlineFileDownload size={20} />
                              <span>
                                {t("downloadBrochure", {
                                  default: "Download PDF",
                                })}
                              </span>
                            </a>
                          </div>
                        )}
                        {activeView === "video" &&
                          previewListingState.listing.has_presentation_video &&
                          resolvedVideo?.src && (
                            <div className={styles.videoWrapper}>
                              <video
                                ref={videoRef}
                                src={resolvedVideo.src}
                                className={`${styles.videoPlayer}`}
                                controls
                                // playsInline
                                poster={resolvedVideo.preview}
                                autoPlay
                                preload="metadata"
                              />
                            </div>
                          )}
                        {activeView === "webtour" &&
                          previewListingState.listing.has_web_tour &&
                          previewListingState.listing.web_tour_url && (
                            <div className={styles.webTourWrapper}>
                              <iframe
                                className={styles.webTourFrame}
                                width="853"
                                height="480"
                                src={previewListingState.listing?.web_tour_url}
                                frameBorder="0"
                                allowFullScreen={true}
                                allow="autoplay; fullscreen; web-share; xr-spatial-tracking;"
                              ></iframe>
                            </div>
                          )}
                        {activeView === "map" &&
                          previewListingState.listing.map_location && (
                            <div className={styles.mapWrapper}>
                              <MapWrapper
                                location={
                                  previewListingState.listing.map_location
                                }
                              />
                            </div>
                          )}
                      </>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className={styles.actions}>
                {photosArray.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleChangeView("photos");
                    }}
                    // 🚀 FIX 2: Use ternary operator to prevent `<button class="false">`
                    className={activeView === "photos" ? styles.active : ""}
                  >
                    <MdPhotoLibrary aria-hidden="true" />
                  </button>
                )}
                {floorPlansArray.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleChangeView("floorplans");
                    }}
                    className={activeView === "floorplans" ? styles.active : ""}
                  >
                    <MdOutlineArchitecture aria-hidden="true" />
                  </button>
                )}
                {previewListingState.listing.has_presentation_video &&
                  resolvedVideo?.src && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleChangeView("video");
                      }}
                      className={activeView === "video" ? styles.active : ""}
                    >
                      <MdOutlineVideoCameraBack aria-hidden="true" />
                    </button>
                  )}
                {previewListingState.listing.web_tour_url &&
                  previewListingState.listing.has_web_tour && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleChangeView("webtour");
                      }}
                      className={activeView === "webtour" ? styles.active : ""}
                    >
                      <BsBadge3D aria-hidden="true" />
                    </button>
                  )}
                {previewListingState.listing.has_brochure &&
                  resolvedBrochure?.src && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleChangeView("brochure");
                      }}
                      className={activeView === "brochure" ? styles.active : ""}
                    >
                      <MdPictureAsPdf aria-hidden="true" />
                    </button>
                  )}
                {previewListingState.listing.map_location && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleChangeView("map");
                    }}
                    className={activeView === "map" ? styles.active : ""}
                  >
                    <MdLocationOn aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>
            {isContactOpen && (
              <ContactDialog
                type={ContactDialogType.LISTING}
                userSub={previewListingState.listing.created_by}
                userType={previewListingState.listing.creator.user_type}
                photoUrl={previewListingState.listing.creator.photo_url}
                userName={previewListingState.listing.creator.name}
                listingId={previewListingState.listing.listing_id}
                listingTitle={previewListingState.listing.title}
                closeCallback={() => {
                  setIsContactOpen(false);
                }}
                phoneNumber={null}
                hasWhatsAppActive={null}
                dialogRef={dialogRef}
              />
            )}
          </div>
        )}
    </>
  );
}

function NextAndBackButtons() {
  const swiper = useSwiper();
  return (
    <>
      <button
        className={styles.backButton}
        onClick={(e) => {
          e.preventDefault();
          swiper.slidePrev();
        }}
      >
        <MdArrowBackIosNew />
      </button>
      <button
        className={styles.nextButton}
        onClick={(e) => {
          e.preventDefault();
          swiper.slideNext();
        }}
      >
        <MdArrowForwardIos />
      </button>
    </>
  );
}
